<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { RBtn } from '@therobotcompany/robco-frontend-library';
import { listLabelingImages, getLabelingLock } from '@/api/RobVisionApi';
import { type LabelingImage, LabelingImageStatus } from '@/models/RobVision';
import ImportImagesDialog from '@/components/robvision/labeling/ImportImagesDialog.vue';
import FineTuneWizard from '@/components/robvision/labeling/FineTuneWizard.vue';
import LabelingEditor from '@/components/robvision/labeling/LabelingEditor.vue';
import { useAlertsStore } from '@/store/AlertsStore';
import { warmSamDecoder } from '@/helpers/SamDecoderSession';
import formatDate from '@/helpers/DateFormatterHelper';
import gettext from '@/i18n/gettext';

const { $gettext } = gettext;
const alertsStore = useAlertsStore();

const props = defineProps<{
  projectUuid: string;
  // For the wizard's model-name suggestion.
  projectName?: string;
  isArchived: boolean;
  // Only one training run per project at a time — the trigger would 409.
  trainingInProgress?: boolean;
}>();

const emit = defineEmits<{
  (e: 'training-triggered'): void;
}>();

const images = ref<LabelingImage[]>([]);
const imagesLoaded = ref(false);
const lockHolder = ref<string | null>(null);
const importDialog = ref(false);
const wizardOpen = ref(false);
const editorOpen = ref(false);
const editorImageUuid = ref<string | undefined>(undefined);

let lockPollTimer: ReturnType<typeof setInterval> | null = null;

const headers = computed(() => [
  { title: $gettext('Image'), key: 'sourceName', sortable: true },
  { title: $gettext('Imported'), key: 'createdAt', sortable: true },
  { title: $gettext('Edited'), key: 'lastModified', sortable: true },
  { title: $gettext('Objects'), key: 'instanceCount', sortable: true },
]);

interface ImageGroup {
  key: string;
  label: string;
  sub: string;
  color: string;
  rows: LabelingImage[];
}

const groups = computed<ImageGroup[]>(() => {
  const byStatus = (statuses: LabelingImageStatus[]) =>
    images.value.filter((image) => statuses.includes(image.status));
  return [
    {
      key: 'todo',
      label: $gettext('To Label'),
      sub: $gettext('imported, waiting for review'),
      color: 'grey',
      rows: byStatus([
        LabelingImageStatus.Unlabeled,
        LabelingImageStatus.Prelabeled,
      ]),
    },
    {
      key: 'in-progress',
      label: $gettext('In Progress'),
      sub: $gettext('partially labeled'),
      color: 'warning',
      rows: byStatus([LabelingImageStatus.InProgress]),
    },
    {
      key: 'ready',
      label: $gettext('Ready'),
      sub: $gettext('reviewed — can go into a dataset'),
      color: 'success',
      rows: byStatus([LabelingImageStatus.Ready]),
    },
  ].filter((group) => group.rows.length > 0);
});

const readyImages = computed(() =>
  images.value.filter((image) => image.status === LabelingImageStatus.Ready),
);

async function fetchImages() {
  try {
    images.value = await listLabelingImages(props.projectUuid);
    if (images.value.length > 0) {
      // Warm the shared SAM decoder (~20 MB first-time download) while the
      // user is still browsing the list, so the editor opens ready.
      void warmSamDecoder();
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    alertsStore.error(error.message);
  } finally {
    imagesLoaded.value = true;
    maybeOpenWizard();
  }
}

// The Models tab's "Train New Model Version" lands here: the request is
// honored once the image list arrived (the tab may have just mounted). With
// nothing trainable the user simply lands on the Labeling tab, where the
// empty state explains the import step.
let wizardRequested = false;

function openWizard() {
  wizardRequested = true;
  maybeOpenWizard();
}

function maybeOpenWizard() {
  if (!wizardRequested || !imagesLoaded.value) {
    return;
  }
  wizardRequested = false;
  if (
    !props.isArchived &&
    !props.trainingInProgress &&
    readyImages.value.length > 0
  ) {
    wizardOpen.value = true;
  }
}

async function fetchLock() {
  // No point polling a tab nobody is looking at; the next visible poll
  // catches up.
  if (document.hidden) {
    return;
  }
  try {
    const lock = await getLabelingLock(props.projectUuid);
    lockHolder.value = lock && !lock.heldByMe ? lock.lockedByName : null;
  } catch {
    lockHolder.value = null;
  }
}

function openEditor(image?: LabelingImage) {
  // Archived projects are read-only; opening would also take the lock.
  if (props.isArchived) {
    return;
  }
  editorImageUuid.value = image?.uuid;
  editorOpen.value = true;
}

function closeEditor() {
  editorOpen.value = false;
  // Statuses/labels likely changed inside the editor.
  fetchImages();
  fetchLock();
}

function formatDateCell(value: string | null): string {
  // The shared helper follows the app's language-driven date convention.
  return value ? formatDate(value, true) : '—';
}

onMounted(() => {
  fetchImages();
  fetchLock();
  lockPollTimer = setInterval(fetchLock, 30_000);
});

onBeforeUnmount(() => {
  if (lockPollTimer) {
    clearInterval(lockPollTimer);
  }
});

defineExpose({ fetchImages, openWizard });
</script>

<template>
  <v-row>
    <v-col cols="12" class="d-flex align-end justify-space-between ga-2">
      <div class="section-intro">
        <div class="d-flex align-center">
          <h3>{{ $gettext('Labeling') }}</h3>
          <v-chip
            v-if="lockHolder"
            size="small"
            color="warning"
            density="comfortable"
            class="ml-3"
          >
            <v-icon icon="mdi-pencil" size="small" class="mr-1" />
            {{
              $gettext('%{ name } is currently annotating', {
                name: lockHolder,
              })
            }}
          </v-chip>
        </div>
        <p class="text-body-2 text-medium-emphasis mb-0">
          {{
            $gettext(
              'Import and label real images from the line, then fine-tune a new model version with them.',
            )
          }}
        </p>
      </div>
      <div class="d-flex align-center ga-2 section-actions">
        <r-btn
          variant="outlined"
          icon="mdi-auto-fix"
          :disabled="
            readyImages.length === 0 || isArchived || trainingInProgress
          "
          @click="wizardOpen = true"
        >
          {{ $gettext('Train New Model Version') }}
        </r-btn>
        <r-btn
          variant="accent"
          icon="mdi-tray-arrow-down"
          :disabled="isArchived"
          @click="importDialog = true"
        >
          {{ $gettext('Import Images') }}
        </r-btn>
      </div>
    </v-col>
  </v-row>

  <v-row v-if="imagesLoaded && images.length === 0">
    <v-col cols="12">
      <v-card rounded="lg" class="pa-8 text-center">
        <v-icon
          icon="mdi-image-multiple-outline"
          size="48"
          class="mb-2 text-medium-emphasis"
        />
        <p class="text-body-1 mb-1">{{ $gettext('No images imported yet') }}</p>
        <p class="text-body-2 text-medium-emphasis mb-0">
          {{
            $gettext(
              'Export results from RobFlow as a .zip and import them here to start labeling.',
            )
          }}
        </p>
      </v-card>
    </v-col>
  </v-row>

  <v-row v-for="group in groups" :key="group.key">
    <v-col cols="12">
      <div class="d-flex align-center mb-2 ml-2">
        <v-icon
          icon="mdi-square-rounded"
          size="x-small"
          :color="group.color"
          class="mr-2"
        />
        <h4 class="mr-2">{{ group.label }}</h4>
        <span class="text-caption text-medium-emphasis">
          {{ group.rows.length }} — {{ group.sub }}
        </span>
      </div>
      <v-card rounded="lg">
        <v-data-table
          :headers="headers"
          :items="group.rows"
          :sort-by="[{ key: 'createdAt', order: 'asc' }]"
          :row-props="() => ({ class: isArchived ? '' : 'cursor-pointer' })"
          items-per-page="-1"
          hover
          density="comfortable"
          @click:row="
            (_event: unknown, row: { item: LabelingImage }) =>
              openEditor(row.item)
          "
        >
          <template #item.sourceName="{ item }">
            <div class="d-flex align-center py-1">
              <v-img
                v-if="item.imageUrl"
                :src="item.imageUrl"
                width="44"
                height="30"
                cover
                rounded="sm"
                class="mr-3 flex-grow-0"
              />
              <v-icon
                v-else
                icon="mdi-image-outline"
                size="small"
                class="mr-3 text-medium-emphasis"
              />
              <span class="font-weight-medium">{{ item.sourceName }}</span>
            </div>
          </template>
          <template #item.createdAt="{ item }">
            <span class="text-body-2">{{
              formatDateCell(item.createdAt)
            }}</span>
          </template>
          <template #item.lastModified="{ item }">
            <span class="text-body-2">{{
              formatDateCell(item.lastModified)
            }}</span>
          </template>
          <template #item.instanceCount="{ item }">
            <span class="text-body-2">{{ item.instanceCount ?? '—' }}</span>
          </template>
          <template #bottom></template>
        </v-data-table>
      </v-card>
    </v-col>
  </v-row>

  <import-images-dialog
    v-model:isOpen="importDialog"
    :project-uuid="projectUuid"
    @imported="fetchImages"
  />
  <fine-tune-wizard
    v-model:isOpen="wizardOpen"
    :project-uuid="projectUuid"
    :project-name="projectName"
    :images="images"
    @training-started="emit('training-triggered')"
  />
  <!-- The editor is a floating card over the app (per design), not a view.
       persistent: closing must go through the X so the lock is released and
       pending edits are flushed deliberately. -->
  <v-dialog
    :model-value="editorOpen"
    content-class="labeling-editor-overlay"
    :transition="false"
    persistent
    no-click-animation
  >
    <labeling-editor
      v-if="editorOpen"
      :project-uuid="projectUuid"
      :initial-image-uuid="editorImageUuid"
      @close="closeEditor"
    />
  </v-dialog>
</template>

<style>
/* Unscoped: v-dialog teleports its content to the overlay container. Pin the
   editor card to fill the screen except the app's nav rail (left) and top
   bar, which render above the overlay, with a 16px gap all around. */
.labeling-editor-overlay {
  position: absolute;
  inset: 80px 16px 16px 16px;
  margin: 0 !important;
  width: auto !important;
  max-width: none !important;
  max-height: none !important;
}
/* The nav rail only exists from MainNavigation's smAndDown breakpoint up —
   below it the rail collapses and the editor may use the full width. */
@media (min-width: 960px) {
  .labeling-editor-overlay {
    inset: 80px 16px 16px 80px;
  }
}
</style>

<style scoped>
/* The button group must sit at the same top-right spot in every language:
   it never shrinks or re-arranges — the intro column absorbs the squeeze
   and wraps its text instead (German labels run ~40% longer). */
.section-intro {
  flex: 1 1 auto;
  min-width: 120px;
}
.section-actions {
  flex-shrink: 0;
}
</style>
